// AdminTestimonialsCard — founder queue for 5-star reactions awaiting approval.
// Renders on the admin dashboard next to sales / signups widgets.
import { useEffect, useState } from "react";
import { Star, Check, X, Quote, Loader2, MessageCircle, Pencil } from "lucide-react";
import { listPendingTestimonials, approveTestimonial, rejectTestimonial } from "@/lib/api";
import { toast } from "sonner";

const Row = ({ t, onApprove, onReject }) => {
  const [displayName, setDisplayName] = useState(t.display_name || "");
  const [role, setRole] = useState(t.role || "");
  const [quote, setQuote] = useState(t.quote || "");
  const [busy, setBusy] = useState(null); // "approve" | "reject" | null

  const approve = async () => {
    setBusy("approve");
    try {
      await approveTestimonial(t.id, { display_name: displayName, role, quote });
      toast.success("Published to /customers");
      onApprove(t.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't approve.");
      setBusy(null);
    }
  };
  const reject = async () => {
    setBusy("reject");
    try {
      await rejectTestimonial(t.id);
      toast.message("Rejected — kept for audit, not published.");
      onReject(t.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't reject.");
      setBusy(null);
    }
  };

  return (
    <div className="rounded-xl border border-border bg-background p-4" data-testid={`admin-testimonial-${t.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-1.5">
          {[0, 1, 2, 3, 4].map((i) => (
            <Star key={i}
              className={`h-3.5 w-3.5 ${i < (t.rating || 5) ? "fill-amber-400 text-amber-400" : "text-muted-foreground/30"}`}
            />
          ))}
          <span className="text-[11px] text-muted-foreground ml-1">{t.user_email}</span>
        </div>
        <span className="text-[10px] uppercase tracking-widest text-muted-foreground">
          {t.created_at ? new Date(t.created_at).toLocaleDateString() : ""}
        </span>
      </div>

      <textarea
        data-testid={`admin-testimonial-quote-${t.id}`}
        value={quote}
        onChange={(e) => setQuote(e.target.value)}
        rows={3}
        className="mt-3 w-full rounded-lg border border-border bg-background p-2.5 text-sm leading-relaxed resize-none"
      />
      <div className="mt-2 grid grid-cols-2 gap-2">
        <input
          data-testid={`admin-testimonial-name-${t.id}`}
          value={displayName} onChange={(e) => setDisplayName(e.target.value)}
          placeholder="Display name"
          className="rounded-lg border border-border bg-background px-2.5 py-1.5 text-xs"
        />
        <input
          data-testid={`admin-testimonial-role-${t.id}`}
          value={role} onChange={(e) => setRole(e.target.value)}
          placeholder="Role / grade"
          className="rounded-lg border border-border bg-background px-2.5 py-1.5 text-xs"
        />
      </div>

      <div className="mt-3 flex gap-2">
        <button
          data-testid={`admin-testimonial-approve-${t.id}`}
          onClick={approve}
          disabled={busy !== null || quote.trim().length < 12}
          className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-full bg-emerald-600 text-white px-3 py-1.5 text-xs font-bold hover:opacity-90 disabled:opacity-50"
        >
          {busy === "approve" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
          Publish to /customers
        </button>
        <button
          data-testid={`admin-testimonial-reject-${t.id}`}
          onClick={reject}
          disabled={busy !== null}
          className="inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-xs font-semibold hover:bg-destructive/10 hover:text-destructive disabled:opacity-50"
        >
          {busy === "reject" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <X className="h-3.5 w-3.5" />}
          Reject
        </button>
      </div>
    </div>
  );
};

export const AdminTestimonialsCard = ({ role }) => {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (role !== "admin") return;
    listPendingTestimonials().then(setData).catch((e) => setErr(e));
  }, [role]);

  if (role !== "admin") return null;
  if (err) return null;
  if (data === null) {
    return (
      <div data-testid="admin-testimonials-card" className="rounded-2xl border border-border bg-card p-6">
        <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading testimonials queue…</div>
      </div>
    );
  }

  const pending = data.pending || [];
  const removeRow = (id) => setData((d) => ({ ...d, pending: (d.pending || []).filter((r) => r.id !== id) }));

  return (
    <div data-testid="admin-testimonials-card" className="rounded-2xl border border-border bg-card p-6">
      <div className="flex items-start gap-3">
        <span className="grid place-items-center h-10 w-10 rounded-xl bg-accent/15 text-accent shrink-0">
          <Quote className="h-5 w-5" />
        </span>
        <div className="flex-1">
          <h3 className="font-serif text-xl font-bold">Testimonials queue</h3>
          <p className="mt-1 text-sm text-muted-foreground">
            5-star reactions from students who opted in. Edit the wording if needed, then publish or reject.
            {" "}<span className="font-semibold text-foreground">{data.approved_count || 0} published so far.</span>
          </p>
        </div>
      </div>

      {pending.length === 0 ? (
        <div className="mt-5 rounded-xl border border-dashed border-border bg-secondary/40 px-4 py-6 text-center text-sm text-muted-foreground">
          <MessageCircle className="h-5 w-5 mx-auto text-accent mb-2" />
          Queue is empty. When a student rates a session 5 stars and opts in, their quote lands here.
        </div>
      ) : (
        <div className="mt-5 space-y-3">
          {pending.map((t) => (
            <Row key={t.id} t={t} onApprove={removeRow} onReject={removeRow} />
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminTestimonialsCard;
